// src/utils/consoleX.js
// *******************************************************************************
// CONSOLE X - Enhanced Console Logger
// *******************************************************************************
// Professional console logging with collapsible groups, color-coding, and stack traces

import { shouldShowLogs } from './helpers';

/**
 * Enhanced console logger with title, colors, and collapsible details
 *
 * Config fields:
 *   message        - Main message to display
 *   flag           - 'i'/'info': Blue | 's'/'success': Green | 'e'/'error': Red |
 *                    'w'/'warning': Orange | 'd'/'debug': Purple | 'p'/'progress': Cyan |
 *                    'c'/'critical': Dark red
 *   title          - Optional title prefix (e.g. 'SSE PARAMETERS')
 *   emoji          - Optional emoji before the message
 *   info           - Optional key-value pairs shown in collapsible group
 *   showInProduction - Show in production (default false)
 *   input          - Interactive input config (localhost only):
 *                    { name, prompt, callback, noPrompt }
 *
 * @example
 * // Simple usage
 * consoleX({ message: 'Connected', flag: 's', title: 'SSE' });
 *
 * @example
 * // With emoji
 * consoleX({
 *   message: 'Tracker registered',
 *   emoji: '🎯',
 *   title: 'API TRACKER',
 *   info: { url: '/api/v1/users' }
 * });
 *
 * @example
 * // With detailed info
 * consoleX({
 *   message: 'Update Received',
 *   flag: 's',
 *   title: 'SSE PARAMETERS',
 *   info: {
 *     'Type': 'update',
 *     'Scope': 'module',
 *     'Module ID': 'welcome',
 *     'Key': 'showNextStepsSession',
 *     'Value': true
 *   }
 * });
 *
 * @example
 * // Force show in production
 * consoleX({
 *   message: 'Critical Error',
 *   flag: 'c',
 *   title: 'PAYMENT',
 *   showInProduction: true,
 *   info: { 'Error': error.message }
 * });
 *
 * @example
 * // With interactive input (localhost only)
 * consoleX({
 *   message: 'Registration Link',
 *   emoji: 'key-icon',
 *   flag: 'progress',
 *   title: 'SECURITY',
 *   input: {
 *     name: 'signup',
 *     prompt: 'Enter email for registration link:',
 *     callback: async (email) => {
 *       const result = await generateRegistrationLink({ email });
 *       return result; // result is logged automatically to the console
 *     }
 *   }
 * });
 * // Then in console, user can type: signup()
 *
 *  Emoji suggestions by context:
 *
 *   Success/Positive:
 *   - Checkmark: completed
 *   - Target: registration
 *   - Rocket: started
 *   - Lightning: fast/optimized
 *
 *   Information:
 *   - Clipboard: list/report
 *   - Package: data/payload
 *   - Link: connection
 *   - Bulb: tip/suggestion
 *
 *   Warning:
 *   - Warning sign: alert
 *   - Skip: ignored
 *   - Reload: reloading
 *   - Pause: paused
 *
 *   Error/Critical:
 *   - X: removed/error
 *   - No entry: blocked
 *   - Fire: critical
 *   - Explosion: fatal failure
 *
 *   Actions:
 *   - Broom: cleanup
 *   - Wrench: configuration
 *   - Palette: style/UI
 *   - Lock: security
 *
 *   EMOJI WEBSTORM: Control + Command + Space
 */
const consoleX = config => {
  const message = config?.message || '';
  const selectedFlag = config?.flag || 'i';
  const selectedTitle = config?.title;
  const emoji = config?.emoji;
  const info = config?.info;
  const color = config?.color;
  let textColor = config?.textColor || 'white';
  const showInProduction = !!config?.showInProduction;
  const input = config?.input;

  // Skip logging if not in a log-enabled environment (localhost or homologation)
  // unless explicitly requested via showInProduction flag
  if (!showInProduction && !shouldShowLogs()) return;

  // Register interactive input function (localhost or homologation environment)
  if (input?.name && input?.callback && typeof window !== 'undefined') {
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHomologation = import.meta.env.VITE_RUNTIME_MODE === 'homologation';
    const isAllowedEnvironment = isLocalhost || isHomologation;

    if (isAllowedEnvironment) {
      if (input.noPrompt) {
        // No user input needed - call callback directly
        window[input.name] = async () => {
          try {
            const result = await input.callback();
            if (result !== undefined) {
              console.log(result); // verify-ignore
              navigateResult(result, input.navigate);
            }
            return result;
          } catch (err) {
            console.error(`[${input.name}] Error:`, err);
            throw err;
          }
        };
      } else {
        window[input.name] = async inputValue => {
          const value = inputValue ?? prompt(input.prompt || `Enter value for ${input.name}:`);
          if (value !== null && value !== '') {
            try {
              const result = await input.callback(value);
              if (result !== undefined) {
                console.log(result); // verify-ignore
                navigateResult(result, input.navigate);
              }
              return result;
            } catch (err) {
              console.error(`[${input.name}] Error:`, err);
              throw err;
            }
          }
          return null;
        };
      }
    }
  }

  // Color palette for different message types
  const colors = {
    info: '#0090fb', // Blue
    warning: '#ff9800', // Orange
    success: '#4caf50', // Green
    error: '#f44336', // Red
    debug: '#9c27b0', // Purple
    progress: '#ffefa2', // Cyan
    critical: '#d32f2f', // Dark Red
    default: '#757575', // Gray
  };

  let selectedColor;

  switch (selectedFlag) {
    case 'i':
    case 'info':
      selectedColor = color || colors.info;
      break;
    case 'w':
    case 'warning':
      selectedColor = color || colors.warning;
      break;
    case 's':
    case 'success':
      selectedColor = color || colors.success;
      break;
    case 'e':
    case 'error':
    case 'f':
    case 'failure':
      selectedColor = color || colors.error;
      break;
    case 'd':
    case 'debug':
      selectedColor = color || colors.debug;
      break;
    case 'p':
    case 'progress':
      selectedColor = color || colors.progress;
      textColor = '#000000';
      break;
    case 'c':
    case 'critical':
      selectedColor = color || colors.critical;
      break;
    default:
      selectedColor = color || colors.default;
  }

  // Build title text with optional emoji
  const emojiPrefix = emoji ? `${emoji} ` : '';
  const titlePrefix = selectedTitle ? `[${selectedTitle?.toUpperCase()}] ` : '';
  const titleText = `%c${emojiPrefix}${titlePrefix}${message}`;
  const titleStyle = `background: ${selectedColor}; color: ${textColor}; font-weight: bold; padding: 4px 8px; border-radius: 4px;`;

  // Build final info object (merge with input instructions if present)
  const finalInfo = { ...info };
  if (input?.name) {
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const isHomologation = import.meta.env.VITE_RUNTIME_MODE === 'homologation';
    const isAllowedEnvironment = isLocalhost || isHomologation;

    if (isAllowedEnvironment) {
      finalInfo['Console Command'] = `${input.name}()`;
      if (!input.noPrompt) {
        finalInfo['Or with value'] = `${input.name}('value')`;
      }
    }
  }

  // If no info provided, just log the title
  if (!finalInfo || typeof finalInfo !== 'object' || Object.keys(finalInfo).length === 0) {
    console.log(titleText, titleStyle); // verify-ignore
    return;
  }

  // With info: use groupCollapsed for expandable details
  console.groupCollapsed(titleText, titleStyle); // verify-ignore

  // Log each info key-value pair
  Object.entries(finalInfo).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      // Pretty print objects (using console.log directly to preserve undefined values)
      console.log(`%c${key}:`, 'font-weight: bold; color: #666;', value); // verify-ignore
    } else if (typeof value === 'boolean') {
      // Highlight booleans (green for true, red for false)
      console.log( // verify-ignore
        `%c${key}: %c${value}`,
        'font-weight: bold; color: #666;',
        `color: ${value ? '#4caf50' : '#f44336'}; font-weight: bold;`
      );
    } else if (typeof value === 'number') {
      // Highlight numbers (blue)
      console.log( // verify-ignore
        `%c${key}: %c${value}`,
        'font-weight: bold; color: #666;',
        'color: #2196f3; font-weight: bold;'
      );
    } else if (typeof value === 'string' && value.length > 100) {
      // Long strings (like JSON payloads) - show in collapsed group
      // console.groupCollapsed(`%c${key}:`, 'font-weight: bold; color: #666;');
      console.log(value); // verify-ignore
      // console.groupEnd();
    } else {
      // Regular string values
      console.log(`%c${key}:`, 'font-weight: bold; color: #666;', value); // verify-ignore
    }
  });

  // Add stack trace at the end
  // console.groupCollapsed(`%cStack:`, 'font-weight: bold; color: #666;');
  // console.trace();
  // console.groupEnd();

  console.groupEnd();
};

export default consoleX;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Navigate to result URL when input.navigate is set.
 * navigate: 'same' - opens in same tab (window.location.href)
 * navigate: 'new'  - opens in new tab (window.open)
 * navigate: false/undefined - no navigation (default)
 */
function navigateResult(result, navigate) {
  if (!navigate || typeof result !== 'string') return;
  const isUrl = result.startsWith('http://') || result.startsWith('https://') || result.startsWith('/');
  if (!isUrl) return;
  if (navigate === 'same') {
    window.location.href = result;
  } else if (navigate === 'new') {
    window.open(result, '_blank');
  }
}
